import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../lib/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ListChecks, SquaresFour, Warning, Clock, User, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { todayLocalISO } from "../lib/dateUtil";
import { fmtDate } from "../lib/dateUtil";

const COLUMNS = [
  { id: "to_do", label: "To Do", statuses: ["to_do", "assigned"], color: "border-t-slate-400" },
  { id: "in_progress", label: "In Progress", statuses: ["in_progress", "awaiting_materials"], color: "border-t-blue-500" },
  { id: "review", label: "Review", statuses: ["completed_by_vendor"], color: "border-t-amber-500" },
  { id: "done", label: "Done", statuses: ["approved"], color: "border-t-emerald-500" },
];

const PRIORITY_COLORS = { urgent: "bg-red-100 text-red-700", high: "bg-amber-100 text-amber-700", medium: "bg-blue-100 text-blue-700", low: "bg-slate-100 text-slate-700" };

export default function KanbanBoardPage() {
  const { projectId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchTasks = useCallback(async () => {
    try {
      const [taskRes, projRes] = await Promise.all([
        API.get(`/tasks?project_id=${projectId}&limit=500`),
        API.get(`/projects/${projectId}`),
      ]);
      setTasks(taskRes.data.tasks || taskRes.data || []);
      setProject(projRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const getColumnTasks = (col) => {
    let t = tasks.filter(task => col.statuses.includes(task.status));
    if (filter !== "all") t = t.filter(task => task.priority === filter);
    return t;
  };

  const STATUS_MAP = {
    to_do: "to_do",
    in_progress: "in_progress",
    review: "completed_by_vendor",
    done: "approved",
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStatus = STATUS_MAP[destination.droppableId];
    if (!newStatus) return;

    const task = tasks.find(t => t.id === draggableId);
    if (!task || task.status === newStatus) return;

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === draggableId ? { ...t, status: newStatus } : t));

    try {
      await API.put(`/tasks/${draggableId}`, { status: newStatus });
      toast.success(`Task moved to ${COLUMNS.find(c => c.id === destination.droppableId)?.label}`);
    } catch {
      toast.error("Failed to update task");
      fetchTasks();
    }
  };

  const today = todayLocalISO();

  return (
    <div data-testid="kanban-board-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to={project ? `/projects/${projectId}` : "/projects"}>
            <Button variant="ghost" size="sm"><ArrowLeft size={16} /></Button>
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Kanban Board</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{project?.name || "Loading..."} - {tasks.length} tasks</p>
          </div>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[130px]" data-testid="kanban-priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" style={{ minHeight: "calc(100vh - 200px)" }}>
            {COLUMNS.map(col => {
              const colTasks = getColumnTasks(col);
              return (
                <div key={col.id} className={`border border-border rounded-md bg-muted/30 flex flex-col border-t-4 ${col.color}`} data-testid={`kanban-col-${col.id}`}>
                  <div className="px-3 py-2.5 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider">{col.label}</h3>
                    <Badge variant="outline" className="text-[10px] h-5">{colTasks.length}</Badge>
                  </div>
                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 px-2 pb-2 space-y-2 overflow-y-auto transition-colors ${snapshot.isDraggingOver ? "bg-primary/5" : ""}`}
                        style={{ minHeight: "100px" }}
                      >
                        {colTasks.map((task, idx) => (
                          <Draggable key={task.id} draggableId={task.id} index={idx}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`bg-card border border-border rounded-md p-3 cursor-grab transition-shadow ${snapshot.isDragging ? "shadow-lg ring-2 ring-primary/30" : "hover:shadow-sm"}`}
                                data-testid={`kanban-task-${task.id}`}
                              >
                                <p className="text-xs font-semibold mb-1 line-clamp-2">{task.title}</p>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>
                                    {task.priority?.toUpperCase()}
                                  </span>
                                  {task.due_date && task.due_date < today && col.id !== "done" && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-0.5">
                                      <Warning size={10} /> Overdue
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-2 text-[10px] text-muted-foreground">
                                  {task.assigned_to_name ? (
                                    <span className="flex items-center gap-1"><User size={10} /> {task.assigned_to_name}</span>
                                  ) : <span>Unassigned</span>}
                                  {task.due_date && <span className="flex items-center gap-1"><Clock size={10} /> {fmtDate(task.due_date)}</span>}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}
    </div>
  );
}
